import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Divider } from '@mui/material';
import { TextField, Typography } from '@totalsoft/rocket-ui';
import { get, set } from '@totalsoft/change-tracking-react';
import { emptyString, femaleDigits, maleGender, femaleGender } from 'constants/defaultValues';
import { valueOrDefault } from 'utils/functions';
import defaultAvatar from 'assets/img/default-avatar.png';
import default_avatar_PJ from 'assets/img/PJ.svg';
import default_avatar_PF from 'assets/img/PF.svg';
import default_avatar_PF_woman from 'assets/img/PFwoman.svg';
import default_avatar_PFA from 'assets/img/PFA.svg';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { OfferDataImage, PartnerImageContainer } from '../PolicyStyles';
import FakeTextWrapper from 'components/common/FakeTextWrapper';

const PartnerOfferDataCard = ({
  partnerLens,
  title,
  showEditableFields,
  disabled,
  readOnly,
  validation,
  validationKey,
  loading,
  isPF,
  isPJ,
  isPFA
}) => {
  const { t } = useTranslation();

  const composeControlId = controlKey => {
    return `${title}.${controlKey}`;
  };

  const getGenderByUniqueId = uniqueId => {
    if (isPF) {
      const firstUniqueIdDigit = uniqueId ? uniqueId.charAt(0) : '1';
      const isFemaleGender = femaleDigits.some(element => element === firstUniqueIdDigit);
      const gender = isFemaleGender ? femaleGender : maleGender;
      return gender;
    }
    return;
  };

  const defaultLogo = isPJ
    ? default_avatar_PJ
    : isPF
    ? getGenderByUniqueId() === maleGender
      ? default_avatar_PF
      : getGenderByUniqueId() === femaleGender
      ? default_avatar_PF_woman
      : defaultAvatar
    : isPFA
    ? default_avatar_PFA
    : defaultAvatar;

  const getPartnerName = () =>
    isPF ? `${(partnerLens.firstName |> get) || ''} ${(partnerLens.lastName |> get) || ''}` : (partnerLens.partnerName |> get) || '';

  return (
    <Grid item container>
      <Grid item container xs={12} md={9} spacing={2}>
        {loading && <FakeTextWrapper lines={2} />}
        {!loading && (
          <>
            <Grid item xs={12} mt={2}>
              <Typography fontWeight='bold' variant='subtitle1'>
                {t(title)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <strong> {t('Offer.Edit.OfferData.Partner.Name')}</strong>: <span> {getPartnerName() || '-'}</span>
            </Grid>
            <Grid item xs={6}>
              <strong> {t('Offer.Edit.OfferData.Partner.Address')}</strong>:{' '}
              <span>
                {' '}
                {t('Offer.Edit.OfferData.Partner.Street')}: {(partnerLens.street |> get) || '-'}
              </span>
              {', '}
              <span>
                {' '}
                {t('Offer.Edit.OfferData.Partner.StreetNumber')}: {(partnerLens.streetNumber |> get) || '-'}
              </span>
              {', '}
              <span>
                {' '}
                {t('Offer.Edit.OfferData.Partner.BuildingNumber')}: {(partnerLens.buildingNumber |> get) || '-'}
              </span>
              {', '}
              <span>
                {' '}
                {t('Offer.Edit.OfferData.Partner.StairNumber')}: {(partnerLens.stairNumber |> get) || '-'}
              </span>
              {', '}
              <span>
                {' '}
                {t('Offer.Edit.OfferData.Partner.FloorNumber')}: {(partnerLens.floorNumber |> get) || '-'}
              </span>
              {', '}
              <span>
                {' '}
                {t('Offer.Edit.OfferData.Partner.FlatNumber')}: {(partnerLens.flatNumber |> get) || '-'}
              </span>
              {', '}
              <span>
                {' '}
                {t('Partner.ZipCode')}: {(partnerLens.zipNumber |> get) || '-'}
              </span>
            </Grid>
          </>
        )}
        {showEditableFields && (
          <Grid item container spacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <TextField
                id={composeControlId('Street')}
                fullWidth={true}
                required={true}
                label={t('Offer.Edit.OfferData.Partner.Street')}
                value={partnerLens.street |> get |> valueOrDefault(emptyString)}
                onChange={partnerLens.street |> set}
                disabled={disabled || readOnly('PolicyOfferData.InsuredClient.Street')}
                error={!isValid(validation[`${validationKey}`].street)}
                helperText={getErrors(validation[`${validationKey}`].street)}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={2}>
              <TextField
                id={composeControlId('StreetNumber')}
                fullWidth={true}
                required={true}
                label={t('Offer.Edit.OfferData.Partner.StreetNumber')}
                value={partnerLens.streetNumber |> get |> valueOrDefault(emptyString)}
                onChange={partnerLens.streetNumber |> set}
                disabled={disabled || readOnly('PolicyOfferData.InsuredClient.StreetNumber')}
                error={!isValid(validation[`${validationKey}`].streetNumber)}
                helperText={getErrors(validation[`${validationKey}`].streetNumber)}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={2}>
              <TextField
                id={composeControlId('BuildingNumber')}
                fullWidth={true}
                label={t('Offer.Edit.OfferData.Partner.BuildingNumber')}
                value={partnerLens.buildingNumber |> get |> valueOrDefault(emptyString)}
                onChange={partnerLens.buildingNumber |> set}
                disabled={disabled || readOnly('PolicyOfferData.InsuredClient.BuildingNumber')}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={1}>
              <TextField
                id={composeControlId('StairNumber')}
                fullWidth={true}
                label={t('Offer.Edit.OfferData.Partner.StairNumber')}
                value={partnerLens.stairNumber |> get |> valueOrDefault(emptyString)}
                onChange={partnerLens.stairNumber |> set}
                disabled={disabled || readOnly('PolicyOfferData.InsuredClient.StairNumber')}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={1}>
              <TextField
                id={composeControlId('FloorNumber')}
                fullWidth={true}
                label={t('Offer.Edit.OfferData.Partner.FloorNumber')}
                value={partnerLens.floorNumber |> get |> valueOrDefault(emptyString)}
                onChange={partnerLens.floorNumber |> set}
                disabled={disabled || readOnly('PolicyOfferData.InsuredClient.FloorNumber')}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={1}>
              <TextField
                id={composeControlId('FlatNumber')}
                fullWidth={true}
                label={t('Offer.Edit.OfferData.Partner.FlatNumber')}
                value={partnerLens.flatNumber |> get |> valueOrDefault(emptyString)}
                onChange={partnerLens.flatNumber |> set}
                disabled={disabled || readOnly('PolicyOfferData.InsuredClient.FlatNumber')}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={1}>
              <TextField
                id={composeControlId('ZipCode')}
                fullWidth={true}
                label={t('Partner.ZipCode')}
                value={partnerLens.zipCode |> get |> valueOrDefault(emptyString)}
                onChange={partnerLens.zipCode |> set}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        )}
        <Grid item container xs={12} />
      </Grid>
      <PartnerImageContainer item container xs={12} md={3}>
        <OfferDataImage src={defaultLogo} alt='...' />
      </PartnerImageContainer>
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>
    </Grid>
  );
};

PartnerOfferDataCard.propTypes = {
  partnerLens: PropTypes.object,
  title: PropTypes.string,
  showEditableFields: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.func,
  validation: PropTypes.object,
  validationKey: PropTypes.string,
  loading: PropTypes.bool,
  isPF: PropTypes.bool,
  isPJ: PropTypes.bool,
  isPFA: PropTypes.bool
};

export default PartnerOfferDataCard;
